import {
  MessageBar,
  MessageBarBody,
  tokens,
} from '@fluentui/react-components';
import { MainContext, useDialogService, useMainContext, useT } from '@powerportalspro/react';
import {
  FileGrid,
  MenuBar,
  RefreshContextButton,
  SaveContextButton,
} from '@powerportalspro/react-fluent';

// Mirror Blazor's `FileGridMainDemo` keys so both stacks share the same
// JSON. Framework flattens to `components.<FullTypeName>.<key>`.
const STRINGS_BASE =
  'components.PowerPortalsPro.Demo.Client.Customizations.Pages.Grids.FileGrid.FileGridMainDemo';

// Same Dataverse view id the Blazor demo pins to. Both stacks run against
// the public demo environment that already has the "Active Files" saved
// query provisioned under this id, so they read from the same view.
const ACTIVE_FILES_VIEW_ID = 'd780631c-23e6-42a5-9c99-1e896a47af49';

// Sentinel thrown from `onBeforeSave` to short-circuit the real save
// round-trip. Caught in `onError` so the user sees the info dialog
// instead of the framework's "Save failed" treatment — matches Blazor's
// `CancelEventArgs.Cancel = true` flow in `FileGridMainDemo`.
const MOCK_SAVE_SENTINEL = '__mock-save-canceled__';

export function FileGridMainSample() {
  const t = useT();
  return (
    <MainContext warnOnUnsavedChanges={false}>
      <MessageBar
        intent="warning"
        layout="multiline"
        style={{ gridTemplateRows: 'auto', minHeight: 'auto', marginBottom: tokens.spacingVerticalM }}
      >
        <MessageBarBody>{t(`${STRINGS_BASE}.login-required`)}</MessageBarBody>
      </MessageBar>
      <FileGridSampleBody />
    </MainContext>
  );
}

// Inner body so it can read MainContext via `useMainContext` and call
// `refreshAll()` from the mock-save's `onError` handler. (Hooks can't
// be called from the same component that mounts the provider.)
function FileGridSampleBody() {
  const t = useT();
  const dialog = useDialogService();
  const ctx = useMainContext();

  return (
    <>
      <MenuBar style={{ marginBottom: tokens.spacingVerticalM }}>
        <SaveContextButton
          appearance="primary"
          onBeforeSave={() => {
            throw new Error(MOCK_SAVE_SENTINEL);
          }}
          onError={async (err: unknown) => {
            const message = err instanceof Error ? err.message : String(err);
            if (message === MOCK_SAVE_SENTINEL) {
              await dialog.showInfoAsync(t(`${STRINGS_BASE}.mock-save-message`));
              await ctx?.refreshAll();
              return;
            }
            await dialog.showErrorAsync(message, { title: 'Save failed' });
          }}
        />
        <RefreshContextButton />
      </MenuBar>
      <FileGrid
        tableName="ppp_file"
        fileColumn="ppp_data"
        allowChangingLayout
        defaultViewId={ACTIVE_FILES_VIEW_ID}
        viewIds={[ACTIVE_FILES_VIEW_ID]}
        maxHeight="450px"
        maxFileSizeBytes={50_000}
      />
    </>
  );
}
